// =====================================================================================
// INVRT Radar — web console — contacts
//
// One list over identified visitors and revealed people (docs/API-CONTRACTS-C1.md §5, §10):
// source tabs, search, a date range and a site filter become the query string GET /contacts
// documents; "Load more" follows the server's opaque next_cursor; the CSV is the server's
// own file with the same filters. A row opens the contact drawer (pages/contacts/), and an
// organisation admin can send a selection by email or push it to a delivery target.
//
// URL state: `source`, `site_id`, `since` and `until` live in the query string so a view
// can be linked to. The search text is local and debounced (300 ms) into `q`, so a person
// typing a name costs one request rather than one per keystroke.
// =====================================================================================

import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Mail, RefreshCw, Send } from "lucide-react";
import { useSites } from "@/hooks/useApi";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input, Select } from "@/components/ui/input";
import { Tabs } from "@/components/ui/tabs";
import { pluralise } from "@/lib/format";
import type { Contact, ContactSourceFilter } from "@/lib/types.c1";
import { CompanyJourneySection } from "./contacts/CompanyJourneySection";
import { ContactDrawer } from "./contacts/ContactDrawer";
import { ContactsTable } from "./contacts/ContactsTable";
import { PushToTargetDialog } from "./contacts/PushToTargetDialog";
import { SendContactsDialog } from "./contacts/SendContactsDialog";
import {
  type ContactsFilters,
  isSourceFilter,
  MAX_CSV_ROWS,
  MAX_SEND_IDS,
  toContactsQuery,
  useContactsCsvDownload,
  useContactsList,
  useDebouncedValue,
  useIsOrgAdmin,
} from "./contacts/useContacts";

const SOURCE_TABS: Array<{ id: ContactSourceFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "identified", label: "Identified visitors" },
  { id: "revealed", label: "Revealed people" },
];

export function ContactsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = useIsOrgAdmin();
  const sites = useSites();
  const csv = useContactsCsvDownload();

  const sourceParam = searchParams.get("source");
  const source: ContactSourceFilter = isSourceFilter(sourceParam) ? sourceParam : "all";
  const siteId = searchParams.get("site_id") ?? "";
  const since = searchParams.get("since") ?? "";
  const until = searchParams.get("until") ?? "";

  const [searchText, setSearchText] = useState(() => searchParams.get("q") ?? "");
  const q = useDebouncedValue(searchText, 300);

  const filters = useMemo<ContactsFilters>(
    () => ({ source, q, siteId, since, until }),
    [source, q, siteId, since, until],
  );
  const contacts = useContactsList(filters);

  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [open, setOpen] = useState<Contact | null>(null);
  const [bulkSend, setBulkSend] = useState(false);
  const [bulkPush, setBulkPush] = useState(false);
  // V03: the company journey dialog. The trigger is remembered at open time so closing
  // (Escape, backdrop or the close button) returns focus to the exact button that opened it.
  const [journeyFor, setJourneyFor] = useState<Contact | null>(null);
  const journeyTriggerRef = useRef<HTMLElement | null>(null);

  function openJourney(contact: Contact): void {
    journeyTriggerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setJourneyFor(contact);
  }

  function closeJourney(): void {
    const trigger = journeyTriggerRef.current;
    journeyTriggerRef.current = null;
    setJourneyFor(null);
    // Re-focus AFTER the dialog has actually closed: while a modal <dialog> is still in the
    // top layer the browser refuses to move focus outside it, so an immediate focus() here
    // is silently overridden. One frame later the dialog is gone and the trigger takes
    // focus deterministically (the Dialog's own close() restore remains as the platform path).
    if (trigger) requestAnimationFrame(() => trigger.focus());
  }

  function updateParams(patch: Record<string, string>): void {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(patch)) {
        if (value === "" || (key === "source" && value === "all")) next.delete(key);
        else next.set(key, value);
      }
      return next;
    });
  }

  const rows = useMemo(
    () => contacts.data?.pages.flatMap((page) => page.contacts) ?? [],
    [contacts.data],
  );
  const selectedIds = useMemo(() => [...selected], [selected]);
  const overCap = selectedIds.length > MAX_SEND_IDS;

  function toggle(id: string): void {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll(ids: string[], checked: boolean): void {
    setSelected((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  const listBody = (() => {
    if (contacts.isLoading) return <LoadingState label="Loading contacts…" />;
    if (contacts.isError) {
      return <ErrorState error={contacts.error} onRetry={() => void contacts.refetch()} />;
    }
    if (!contacts.data) return null;
    if (rows.length === 0) {
      const filtered = q.trim() !== "" || siteId !== "" || since !== "" || until !== "" || source !== "all";
      return (
        <EmptyState
          title={filtered ? "No contacts match these filters" : "No contacts yet"}
          body={
            filtered
              ? "Widen the date range, clear the search, or look across every source."
              : "Identified visitors appear here once a site's pixel is live, and revealed people the moment you reveal them on the Find people page."
          }
        />
      );
    }
    return (
      <div className="flex flex-col gap-4">
        <ContactsTable
          contacts={rows}
          selectable={isAdmin}
          selected={selected}
          onToggle={toggle}
          onToggleAll={toggleAll}
          onOpen={setOpen}
          onOpenJourney={openJourney}
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-ink-muted">
            Showing {pluralise(rows.length, "contact")}
            {contacts.hasNextPage ? ", more available" : ""}.
          </p>
          {contacts.hasNextPage ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void contacts.fetchNextPage()}
              loading={contacts.isFetchingNextPage}
            >
              Load more
            </Button>
          ) : null}
        </div>
      </div>
    );
  })();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Contacts</h1>
          <p className="mt-1 text-sm text-ink-muted">
            The people Radar has identified on your sites and the people you have revealed,
            together in one place.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            variant="secondary"
            onClick={() => csv.mutate(toContactsQuery(filters))}
            loading={csv.isPending}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Download CSV
          </Button>
          <p className="text-xs text-ink-muted">
            The current filters, up to {MAX_CSV_ROWS.toLocaleString("en-GB")} rows.
          </p>
        </div>
      </div>

      {csv.isError ? <ErrorState error={csv.error} /> : null}

      <Tabs
        activeId={source}
        onChange={(next) => {
          setSelected(new Set());
          updateParams({ source: next });
        }}
        items={SOURCE_TABS.map((tab) => ({
          id: tab.id,
          label: tab.label,
          content: (
            <div className="flex flex-col gap-5">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Input
                  label="Search"
                  type="search"
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder="Name, email, company or domain"
                />
                <Select
                  label="Site"
                  value={siteId}
                  onChange={(event) => updateParams({ site_id: event.target.value })}
                >
                  <option value="">All sites</option>
                  {(sites.data?.sites ?? []).map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.domain}
                    </option>
                  ))}
                </Select>
                <Input
                  label="From"
                  type="date"
                  value={since}
                  max={until === "" ? undefined : until}
                  onChange={(event) => updateParams({ since: event.target.value })}
                />
                <Input
                  label="To"
                  type="date"
                  value={until}
                  min={since === "" ? undefined : since}
                  onChange={(event) => updateParams({ until: event.target.value })}
                />
              </div>

              {isAdmin && selectedIds.length > 0 ? (
                <div
                  role="region"
                  aria-label="Selection"
                  className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-primary-200 bg-primary-50 px-4 py-3"
                >
                  <div className="flex flex-col">
                    <p className="text-sm font-medium text-ink">{selectedIds.length} selected</p>
                    {overCap ? (
                      <p className="text-xs text-danger">
                        Up to {MAX_SEND_IDS} contacts can be sent in one go.
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" onClick={() => setBulkSend(true)} disabled={overCap}>
                      <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                      Send by email
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setBulkPush(true)}
                      disabled={overCap}
                    >
                      <Send className="h-3.5 w-3.5" aria-hidden="true" />
                      Push to a target
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                      Clear selection
                    </Button>
                  </div>
                </div>
              ) : null}

              <div className="flex items-center justify-end">
                <Button variant="ghost" size="sm" onClick={() => void contacts.refetch()}>
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                  Refresh
                </Button>
              </div>

              {listBody}
            </div>
          ),
        }))}
      />

      {open ? (
        <ContactDrawer contactId={open.id} initial={open} onClose={() => setOpen(null)} />
      ) : null}

      {journeyFor ? (
        <Dialog
          open
          onClose={closeJourney}
          title={`Company journey - ${journeyFor.company.name ?? journeyFor.company.domain ?? "this company"}`}
          description="The company's visitor journey and its list-sourced roster contacts, kept visibly apart."
          className="max-h-[min(48rem,calc(100vh-2rem))] w-[min(48rem,calc(100vw-2rem))] overflow-y-auto"
        >
          <CompanyJourneySection
            {...(journeyFor.company.domain
              ? { domain: journeyFor.company.domain }
              : { name: journeyFor.company.name as string })}
          />
        </Dialog>
      ) : null}

      <SendContactsDialog
        open={bulkSend}
        contactIds={selectedIds}
        onClose={() => setBulkSend(false)}
        onSent={() => setSelected(new Set())}
      />
      <PushToTargetDialog
        open={bulkPush}
        contactIds={selectedIds}
        onClose={() => setBulkPush(false)}
        onPushed={() => setSelected(new Set())}
      />
    </div>
  );
}
